// ===========================================
// Report keys — the ONE list (frontend mirror)
// ===========================================
// ⚠️ Duplicated DELIBERATELY from
// apps/backend/src/constants/reportKeys.constants.ts — this monorepo does not
// do cross-workspace imports. Keep both files in step; they are pinned by
// apps/backend/tests/unit/constants/reportKeys.parity.test.ts.
//
// ⚠️ A key MUST equal the report's route path segment under /admin/reports, so
// nav (AdminLayout.tsx), the Overview page, the Report settings page and the
// scheduled digest all agree on one identifier. `enrollment` keeps its US
// spelling because the ROUTE does; the user-facing label is
// "Enrolment Analytics".

export const REPORT_KEYS = [
  'attendance',
  'revenue',
  'outstanding-invoices',
  'student-progress',
  'teacher-performance',
  'hybrid-booking',
  'enrollment',
  'operations',
  'email-log',
  'retro-edits',
] as const;

export type ReportKey = (typeof REPORT_KEYS)[number];

/**
 * How each report answers "what period does this cover?" — see the backend
 * copy for the full rationale. Summary: a rolling "last 7 days" digest cannot
 * be honoured by every report (hybrid booking takes a REQUIRED termId and 400s
 * without one; enrolment and parts of operations take a `months` count), so
 * every digest section states the period it actually covers.
 */
export const REPORT_PERIOD_SHAPE: Record<ReportKey, 'windowed' | 'snapshot' | 'term'> = {
  attendance: 'windowed',
  revenue: 'windowed',
  'student-progress': 'windowed',
  'teacher-performance': 'windowed',
  'email-log': 'windowed',
  'retro-edits': 'windowed',
  'outstanding-invoices': 'snapshot',
  'hybrid-booking': 'term',
  enrollment: 'term',
  operations: 'term',
};

/** Human labels, used by the settings page and the digest's section headings. */
export const REPORT_LABELS: Record<ReportKey, string> = {
  attendance: 'Attendance',
  revenue: 'Revenue',
  'outstanding-invoices': 'Outstanding Invoices',
  'student-progress': 'Student Progress',
  'teacher-performance': 'Teacher Performance',
  'hybrid-booking': 'Hybrid Booking Analytics',
  enrollment: 'Enrolment Analytics',
  operations: 'Operational Efficiency',
  'email-log': 'Email Log',
  'retro-edits': 'Retro Edits',
};

// NOTE: an `isReportKey` type guard was written here and REMOVED at /qa
// (2026-09-16) — it had zero callers on either side. See the backend copy for
// the reasoning. Keep the two files in step: the parity test compares
// REPORT_KEYS / REPORT_PERIOD_SHAPE / REPORT_LABELS, not helper functions, so a
// helper re-added to only one side would NOT be caught.
